import { DOMAIN, domainCountry } from '@/src/constants/domain'
import { DOMAIN_CURRENCY_MAP } from '@/src/constants/domainCurrencyMap'
import { MulticitySearchBody, MulticitySector, QueryInput } from '@/src/types/searchQuery'
import { validSearchQueryDateFormats } from '@/src/utils/date'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const MAX_MULTICITY_SEGMENTS = 10

const parseMulticityDate = (dateStr?: string): dayjs.Dayjs | null => {
    if (!dateStr?.trim()) return null
    for (const format of validSearchQueryDateFormats) {
        const parsed = dayjs(dateStr, format)
        if (parsed.isValid()) return parsed
    }
    return null
}

/**
 * Detects whether the current SRP URL is a multicity search.
 * Multicity uses dep{n}/arr{n} segment params instead of a single from/to.
 */
export const isMulticityQuery = (query: Record<string, string>): boolean =>
    !!query?.dep1?.trim() && !!query?.arr1?.trim()

const getSellingContext = (): { sct: string; scr: string; source: string } => {
    const host = (() => {
        try {
            return new URL(DOMAIN).host
        } catch {
            return ''
        }
    })()
    const scr = DOMAIN_CURRENCY_MAP[host]?.currency || 'SAR'
    return { sct: domainCountry, scr, source: 'B2C' }
}

const resolveCabinClass = (query: Record<string, string>): string => {
    const rawClass = query?.class?.trim()
    return rawClass ? rawClass.toUpperCase().replace(/\s+/g, '_') : 'ECONOMY'
}

/**
 * Parses multicity sector params (dep{n}/arr{n}/depdate{n}) into normalized sectors.
 * Empty slots are skipped; dates are normalized to DD/MM/YYYY.
 */
export const parseMulticitySectors = (
    query: Record<string, string>
): { sectors: MulticitySector[]; hasInvalidDate: boolean } => {
    const sectors: MulticitySector[] = []
    let hasInvalidDate = false

    for (let i = 1; i <= MAX_MULTICITY_SEGMENTS; i++) {
        if (query[`dep${i}`] === undefined && query[`arr${i}`] === undefined) break
        const from = query[`dep${i}`]?.trim()?.toUpperCase()
        const to = query[`arr${i}`]?.trim()?.toUpperCase()
        const rawDate = query[`depdate${i}`]?.trim()
        if (!from && !to && !rawDate) continue

        const parsedDate = parseMulticityDate(rawDate)
        if (!parsedDate) hasInvalidDate = true
        sectors.push({
            from: from || '',
            to: to || '',
            depart_date: parsedDate ? parsedDate.format('DD/MM/YYYY') : ''
        })
    }

    return { sectors, hasInvalidDate }
}

/**
 * Builds the POST body for the multicity results API from the SRP URL params.
 */
export const getMulticitySearchBody = (query: Record<string, string>): MulticitySearchBody => {
    const { sectors } = parseMulticitySectors(query)
    const { sct, scr, source } = getSellingContext()

    return {
        sectors,
        adults: Number(query.adults ?? 0),
        childs: Number(query.childs ?? 0),
        infants: Number(query.infants ?? 0),
        class: resolveCabinClass(query),
        sct,
        scr,
        source
    }
}

export const getSearchQueryObj = (query: QueryInput) => {
    const {
        from,
        origin,
        sourceCountry,
        to,
        destination,
        destinationCountry,
        adults,
        childs,
        infants,
        depart_date,
        return_date,
        class: flightClass,
        intl,
        sd,
        airline,
        carrier,
    } = query

    const queryObj: Record<string, string> = {
        from,
        to,
        origin,
        destination,
        depart_date,
        class: flightClass,
        adults,
        childs,
        infants,
        mobileApp: 'true',
        intl,
        responseType: 'jsonV3',
        // source: 'MOBILE',
        utm_currency: 'SAR',
        dc_enabled: 'true',
    }
    if (return_date) {
        queryObj.return_date = return_date
    }
    if (airline) {
        queryObj.airline = airline
    }
    if (carrier) {
        queryObj.carrier = carrier
    }
    if (sd) {
        queryObj.sd = sd
    }

    return queryObj
}
